/**
 * ConfigService class provides various configuration and utility methods for the application.
 * It initializes with several dependencies and provides methods to check modes, get paths, and more.
 */
class ConfigService {
    /**
     * Constructor for ConfigService.
     * @param {Object} $signals - Signal service.
     * @param {Object} $injector - AngularJS injector service.
     * @param {Object} $route - AngularJS route service.
     * @param {Object} $rootScope - AngularJS root scope.
     * @param {Object} $sessionStorage - Session storage service.
     * @param {Object} $translate - Translation service.
     */
    constructor($signals, $injector, $route, $rootScope, $sessionStorage, $translate) {
        this.$signals = $signals;
        this.$injector = $injector;
        this.$route = $route;
        this.$rootScope = $rootScope;
        this.$sessionStorage = $sessionStorage;
        this.$translate = $translate;

        // Meta setup
        this._logIdentifier = 'ConfigService';
        this.LogBridgeService = $injector.get('LogBridgeService');
        this.AuthenticationBridgeService = null;
        this.BridgeRuntimeService = null;
        this.$logger = this.LogBridgeService;
        this._journalTime = null;
        this._readingTime = null; // Added initialization for _readingTime

        this.$logger.info(this, 'initialize');
    }

    /**
     * Checks if the application is in preview mode.
     * @returns {boolean} - True if in preview mode, false otherwise.
     */
    isPreviewMode() {
        if (typeof(this.$route.current) === 'undefined' || typeof(this.$route.current.params) === 'undefined') {
            return false;
        }
        return this.$route.current.params.preview === 'preview';
    }

    /**
     * Checks if the application is in PDF mode.
     * @returns {boolean} - True if in PDF mode, false otherwise.
     */
    isPDFMode() {
        if (this.$route.current && this.$route.current.regexp) {
            return this.$route.current.regexp.test("/pdf/:taskUUID/:sheetUUID") ||  
                   this.$route.current.regexp.test("/pdf/:taskUUID/:sheetUUID/:pagebreak") ||  
                   this.$route.current.regexp.test("/pdf/:taskUUID/:sheetUUID/qpart/:questionPartUUID");
        }
        return false;
    }

    /**
     * Checks if the application is on an edit sheet.
     * @returns {boolean} - True if on an edit sheet, false otherwise.
     */
    isOnEditSheet() {
        if (this.$route.current && this.$route.current.regexp) {
            return this.$route.current.regexp.test("/overview/:taskUUID") ||
                this.$route.current.regexp.test("/overview/:taskUUID/:preview") ||
                this.$route.current.regexp.test("/sheet/:taskUUID/:sheetUUID") ||
                this.$route.current.regexp.test("/sheet/:taskUUID/:sheetUUID/:scrollToUUID/:preview") ||
                this.$route.current.regexp.test("/sheet/:taskUUID/:sheetUUID/:preview") ||
                this.$route.current.regexp.test("/mediamanager/:taskUUID") ||
                this.$route.current.regexp.test("/mediamanager/:taskUUID/:returnTo") ||
                this.$route.current.regexp.test("/instructions/:taskUUID") ||
                this.$route.current.regexp.test("/instructions/:taskUUID/:returnTo");
        }
        return false;
    }

    /**
     * Checks if the application is in exam export mode.
     * @returns {boolean} - True if in exam export mode, false otherwise.
     */
    isExamExport() {
        if (this.$route.current && this.$route.current.regexp) {
            return this.$route.current.regexp.test("/exam-export/:taskUUID/");
        }
        return false;
    }

    /**
     * Gets the reading time.
     * @returns {number} - The reading time in milliseconds.
     */
    getReadingTime() {
        if (typeof this._readingTime === 'undefined' || this._readingTime === null) {
            this.setReadingTime(window.IB.timers.maxReadingTime);
        }
        return this._readingTime;
    }

    /**
     * Sets the reading time.
     * @param {number} val - The reading time in milliseconds.
     */
    setReadingTime(val) {
        this.$logger.info(this, 'setting reading time to ' + (val / 1000 / 60) + ' minutes');
        this._readingTime = val;
    }

    /**
     * Gets the journal time.
     * @returns {number} - The journal time in milliseconds.
     */
    getJournalTime() {
        if (typeof this._journalTime === 'undefined' || this._journalTime === null) {
            this.setJournalTime(window.IB.timers.maxJournalTime);
        }
        return this._journalTime;
    }

    /**
     * Sets the journal time.
     * @param {number} val - The journal time in milliseconds.
     */
    setJournalTime(val) {
        this.$logger.info(this, 'setting journal time to ' + (val / 1000 / 60) + ' minutes');
        this._journalTime = val;
    }

    /**
     * Gets the IAA types.
     * @returns {Object} - An object containing IAA types.
     */
    getIAATypes() {
        return {
            default: this.$translate.instant("CONTENTOBJECT.IAA-CONTAINER.IAA-CONTAINER.default"),
            visuallyImpaired: this.$translate.instant("CONTENTOBJECT.IAA-CONTAINER.IAA-CONTAINER.visuallyImpaired")
        };
    }

    /**
     * Checks if the application is in assessment mode.
     * @returns {boolean} - True if in assessment mode, false otherwise.
     */
    inAssessment() {
        if (typeof this.$route.current.params === 'undefined') {
            return false;
        }
        if (this.$route.current.params.taskUUID && !this.$route.current.regexp.test("/wrap-and-deploy-details/:taskUUID")) {
            return true;
        }
        return false;
    }

    /**
     * Checks if the application is in sheet mode.
     * @returns {boolean} - True if in sheet mode, false otherwise.
     */
    inSheet() {
        if (typeof this.$route.current.params === 'undefined') {
            return false;
        }
        if (this.$route.current.params.sheetUUID && this.inAssessment()) {
            return true;
        }
        return false;
    }

    /**
     * Gets the public folder path.
     * @returns {string} - The public folder path.
     */
    getPublicFolderPath() {
        return '/public/';
    }

    /**
     * Gets the task content folder path.
     * @returns {string} - The task content folder path.
     */
    getTaskContentFolderPath() {
        return '/taskcontent/';
    }

    /**
     * Gets the user folder path.
     * @returns {string} - The user folder path.
     */
    getUserFolderPath() {
        if (isCMS) {
            return '/authentication/api_session/';
        }
        return '/user/';
    }

    /**
     * Gets the response data path.
     * @returns {string} - The response data path.
     */
    getResponseDataPath() {
        return '/responses-unpacked/';
    }

    /**
     * Gets the task info URL.
     * @returns {string} - The task info URL.
     */
    getTaskInfoURL() {
        if (isCMS || this.$rootScope.isTestingMode) {
            let uuidRoute;
            if (this.$rootScope.isTestingMode) {
                uuidRoute = this.$sessionStorage.usedAssessment;
            } else {
                uuidRoute = this.$route.current.params.taskUUID;
            }

            if (this.isPDFMode()) {
                return this.cms_getAPIURL() + 'taskinfo/' + uuidRoute + '/';
            }
            if (!angular.isDefined(uuidRoute)) {
                throw "Couldn't find task UUID in URL!!!";
            }
            return this.cms_getAPIURL() + 'taskinfo/' + uuidRoute + '/';
        } else {
            return this.getPublicFolderPath() + 'taskinfo.json';
        }
    }

    /**
     * Gets the user file URL.
     * @param {string} userUUID - The user UUID.
     * @returns {string} - The user file URL.
     */
    getUserFileURL(userUUID) {
        if (isCMS) {
            return this.getUserFolderPath();
        }
        if (this.$sessionStorage.user_data_path) {
            return "/admintools/responsefile/" + this.$sessionStorage.user_data_path;
        }

        return this.getUserFolderPath() + userUUID + '.json';
    }

    /**
     * Gets the media URL.
     * @param {string} path - The media path.
     * @returns {string} - The media URL.
     */
    getMediaURL(path) {
        if (!this.BridgeRuntimeService) {
            this.BridgeRuntimeService = this.$injector.get('BridgeRuntimeService');
        }
        const r = this.BridgeRuntimeService;
        return r.getMediaUrl() + "/media/" + path;
    }

    /**
     * Gets the options file URL.
     * @returns {string} - The options file URL.
     */
    getOptionsFileURL() {
        if (isCMS || this.$sessionStorage.user_data_path) {
            console.error('TODO: Fix this url');
            return '/cms/options';
        }
        return this.getPublicFolderPath() + 'options.json';
    }

    /**
     * Gets the response folder URL.
     * @param {string} userUUID - The user UUID.
     * @param {string} schoolCode - The school code.
     * @param {string} responseFolderName - The response folder name.
     * @returns {string} - The response folder URL.
     */
    getResponseFolderURL(userUUID, schoolCode, responseFolderName) {
        return this.getResponseDataPath() + schoolCode + '/' + responseFolderName;
    }

    /**
     * Gets the response file URL.
     * @param {string} userUUID - The user UUID.
     * @param {string} schoolCode - The school code.
     * @param {string} responseFolderName - The response folder name.
     * @returns {string} - The response file URL.
     */
    getResponseFileURL(userUUID, schoolCode, responseFolderName) {
        return this.getResponseFolderURL(userUUID, schoolCode, responseFolderName) + '/ResponseData.json.rdk.dec';
    }

    /**
     * Gets the CMS API URL.
     * @returns {string} - The CMS API URL.
     */
    cms_getAPIURL() {
        const url = '/api/v1/';
        return url;
    }

    /**
     * Gets the supported browser list.
     * @returns {Object} - An object containing supported browsers and their minimum versions.
     */
    getSupportedBrowserList() {
        return {
            'safari': 5,
            'chrome': 18,
            'firefox': 15,
        };
    }

    /**
     * Gets the date format.
     * @param {string} name - The name of the date format.
     * @returns {string} - The date format.
     */
    getDateFormat(name) {
        const dateFormats = {
            'HumanTime': 'hh:mm A',
            'HumanShortTime': 'hh:mm:ss A',
            'HumanDate': 'YYYY-MM-dd',
            'HumanDateUK': 'DD/MM/YYYY',
            'HumanDateTimeUK': 'DD/MM/YYYY hh:mm A',
            'HumanShortDate': 'D. MMM, YYYY',
            'HumanLongDate': 'D. MMMM, YYYY',
            'HumanDateTime': 'YYYY-MM-dd hh:mm:ss a',
            'HumanShortDateTime': 'YYYY-MM-dd hh:mm a'
        };
        return dateFormats[name];
    }

    /**
     * Gets the content object types.
     * @returns {Array} - An array of content object types.
     */
    getContentObjectTypes() {
        return [
            'text',
            'audio',
            'response-textbox',
            'response-short-textbox',
            'auto-plot-from-table',
            'iframe',
            'image',
            'measurement',
            'scattergraph',
            'universalcanvas',
            'video',
            'wiris',
            'geogebra'
        ];
    }

    /**
     * Gets the advanced object types list.
     * @returns {Array} - An array of advanced object types.
     */
    getAdvancedObjectTypesList() {
        return ['universalcanvas', 'measurement', 'scattergraph'];
    }

    /**
     * Gets the special object container list.
     * @returns {Array} - An array of special object containers.
     */
    getSpecialObjectContainerList() {
        return ['tabs', 'carousels'];
    }
}

export default ConfigService;